import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookConsultModal from '../components/BookConsultModal';
import { Button } from '../components/ui/button';
import { Calendar, Users, Shield, Award, CheckCircle } from 'lucide-react';

const AboutPage = () => {
  const [showModal, setShowModal] = useState(false);

  const values = [
    { icon: Users, title: 'Client-First', desc: 'We prioritize your needs and speak in plain language, not tech jargon.' },
    { icon: Shield, title: 'Security-Focused', desc: 'Every decision we make considers the security implications for your business.' },
    { icon: Award, title: 'Reliable', desc: 'We show up when we say we will and follow through on our commitments.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">About Microsystems Networking</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're a small team of IT professionals who specialize in helping small businesses and nonprofits get reliable, secure technology without the enterprise price tag.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Story</h2>
          <div className="prose prose-lg max-w-none text-gray-600">
            <p className="mb-6">
              Microsystems Networking was founded with a simple idea: small businesses and nonprofits deserve the same quality IT support that large enterprises get, but sized and priced appropriately for their needs.
            </p>
            <p className="mb-6">
              With over 25 years of combined experience in IT, we've worked with organizations of all sizes. We noticed that smaller organizations often got stuck with two bad options: expensive enterprise solutions they didn't need, or unreliable break-fix support that left them vulnerable.
            </p>
            <p>
              We built Microsystems Networking to be the third option—professional, proactive IT management that's right-sized for organizations with 1-50 users. We focus on Microsoft 365, network infrastructure, and cybersecurity because that's what most small organizations need to run smoothly and securely.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Area</h2>
          <p className="text-lg text-gray-600 mb-8">
            We primarily serve organizations in the South Shore and Greater Boston area of Massachusetts. Remote support is available for clients anywhere.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['South Shore', 'Greater Boston', 'Plymouth County', 'Norfolk County', 'Bristol County'].map((area, index) => (
              <span key={index} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Work Together?</h2>
          <p className="text-xl text-blue-50 mb-8">
            Let's discuss how we can help your organization.
          </p>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" onClick={() => setShowModal(true)}>
            <Calendar className="w-5 h-5 mr-2" />
            Book a Free Consult
          </Button>
        </div>
      </section>

      <Footer />
      <BookConsultModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default AboutPage;
